import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import {
  ButtonUI,
  CustomThemeProvider,
  InputSearch,
  ModalUI,
  Selector,
  showNotification,
  TaptapList,
  TooltipButton,
  usePortal,
  useQueryFetch
} from 'sonata-react-components';

import { DateRange } from '@/components/campaigns/ListFilters/DateRange';
import { getPixelTypeOptions } from '@/components/menu/MyPixels/helpers';
import { getPixels } from '@/services/pixel.service';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';

import './ConversionTrackingModal.scss';

import { copyPixelCodeToClipboard } from '@/helpers/pixel/pixel.helper';

import type { Pixel } from 'sonata-static-storage/lib/schemas/pixel/pixel.schema';

type Props = {
  id?: string;
  isModalOpened: boolean;
  onCloseModal: () => void;
  onSavePixels: (selectedRows: Pixel[]) => void;
  selectedPixels?: string[];
};

type TaptapListItem = {
  actions: any[];
  campaigns: number;
  creationDate: string;
  id: string;
  title: string;
  type: string;
};

type PixelTypeOption = {
  id: string;
  label: string;
};

type DateRangeValue = {
  beginDate?: string;
  endDate?: string;
};

type Filters = {
  pixelTypes: PixelTypeOption[];
  name: string;
  dates: DateRangeValue;
};

type State = {
  filters: Filters;
  selectedPixelsRows: string[];
};

export default function ConversionTrackingModal({
  isModalOpened,
  onCloseModal,
  onSavePixels,
  selectedPixels,
  id
}: Props) {
  const { formatMessage } = useIntl();
  const targetBody = usePortal('creativesModal');
  const modalRef = useRef<any>(null);

  const user = useGlobalStore(getUser);
  const userJSON = user.toJSON();
  const userRoles = useGlobalStore(getUserRoles);
  const token: Record<string, any> = {};

  useEffect(() => {
    if (isModalOpened) {
      modalRef?.current?.openModal?.();
    } else {
      modalRef?.current?.closeModal?.();
    }
  }, [isModalOpened]);

  const [{ filters, selectedPixelsRows }, setState] = useState<State>({
    filters: {
      dates: {},
      name: '',
      pixelTypes: []
    },
    selectedPixelsRows: selectedPixels || []
  });

  const { data } = useQueryFetch(
    ['pixels', isModalOpened, filters.pixelTypes, filters.dates, filters.name],
    async () => {
      const pixelTypeIds = filters.pixelTypes.map(pixelType => pixelType.id as Pixel['type']);

      const finalFilterType = pixelTypeIds.length > 0 ? pixelTypeIds : undefined;
      const payload = {
        date: filters.dates,
        name: filters.name,
        type: finalFilterType
      };
      const pixels = await getPixels(payload, userJSON.id!, token);
      return pixels.items;
    },
    {
      cacheTime: 0,
      staleTime: 0
    }
  );

  const pixelToRow = (pixel: Pixel) => {
    return {
      actions: [
        {
          content: (
            <TooltipButton
              buttonConfig={{
                buttonOnClick: () => handleCopyPixelCode(pixel),
                buttonShape: 'link',
                buttonSize: 'small',
                buttonType: 'secondary'
              }}
              icon="fa-solid fa-code"
              id={`conversionTrackingModal-tooltip-button-${pixel._id}`}
              isButton={true}
              tooltipConfig={{
                tooltipEvent: 'mouseenter',
                tooltipPlace: 'left'
              }}
            >
              {formatMessage({
                defaultMessage: "Copy Pixel's code",
                id: 'conversionTracking.modal.action.copy'
              })}
            </TooltipButton>
          ),
          id: `custom-action-edit-tooltip-${pixel._id}`,
          isCustomAction: true
        }
      ],
      campaigns: pixel.campaigns?.length || 0,
      creationDate: moment(pixel.created).format('DD/MM/YY - HH:mm'),
      id: pixel._id!,
      title: pixel.name,
      type: formatMessage({
        defaultMessage:
          String(pixel.type).toLowerCase() === 'stay'
            ? 'Permanence'
            : pixel.type.charAt(0).toUpperCase() + String(pixel.type).slice(1),
        id: `conversionTracking.table.pixelType.${String(pixel.type).toLowerCase()}`
      })
    };
  };

  const fetchPixelsPaginated = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number }) => {
      const pixelTypeIds = filters.pixelTypes.map(pixelType => pixelType.id as Pixel['type']);
      const payload = {
        date: filters.dates,
        itemsPerPage: pageSize.toString(),
        name: filters.name,
        page: page.toString(),
        type: pixelTypeIds.length > 0 ? pixelTypeIds : undefined
      };
      const pixels = await getPixels(payload, userJSON.id!, token);
      return {
        data: pixels.items.map(pixelToRow),
        page: pixels.currentPage ?? page,
        pageSize: pixels.itemsPerPage ?? pageSize,
        total: pixels.totalItems ?? pixels.items.length,
        totalPages: Math.max(
          1,
          Math.ceil((pixels.totalItems ?? pixels.items.length) / (pixels.itemsPerPage ?? pageSize))
        )
      };
    },
    [filters, userJSON.id]
  );

  const handleFilterChange = <K extends keyof Filters>(filterName: K, value: Filters[K]) => {
    setState(prevState => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        [filterName]: value
      }
    }));
  };

  const handleCopyPixelCode = async (pixel: Pixel) => {
    await copyPixelCodeToClipboard({ pixel });
    showNotification(
      'map',
      formatMessage({
        defaultMessage: 'Pixel code copied to clipboard',
        id: 'conversionTracking.modal.action.copied'
      })
    );
  };

  const handleDateRangeChange = (dateFilters: { id: string; value: DateRangeValue }[]) => {
    const dateValue = dateFilters.find(f => f.id === 'dates')?.value || {};
    handleFilterChange('dates', dateValue);
    return dateFilters;
  };

  const handleSelectionChange = (selectedItems: any[]) => {
    setState(prevState => ({
      ...prevState,
      selectedPixelsRows: selectedItems.map((item: any) => item.id)
    }));
  };

  const handlePixelSave = () => {
    if (!data) {
      return;
    }
    const selected = data.filter((pixel: Pixel) => selectedPixelsRows.includes(pixel._id!));
    onSavePixels(selected);
    onCloseModal();
  };

  const pixelTypeOptions = getPixelTypeOptions(userJSON, userRoles, formatMessage);

  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast="light">
      <ModalUI
        closeModalCallback={onCloseModal}
        leftButton={
          <ButtonUI
            onClick={onCloseModal}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Cancel',
              id: 'common.cancel'
            })}
            type="secondary"
          />
        }
        ref={modalRef}
        rightButton={
          <ButtonUI
            icon="fa-solid fa-plus"
            onClick={handlePixelSave}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Add pixels',
              id: 'conversionTracking.modal.button.add'
            })}
            type="primary"
          />
        }
        title={formatMessage({
          defaultMessage: 'Conversion Tracking',
          id: 'conversionTracking.modal.title'
        })}
        width="954px"
      >
        <div className="conversionTrackingModal__filters">
          <Selector
            className="conversionTrackingModal__selector"
            id="conversionTrackingModal-pixelTypeFilter"
            label={formatMessage({
              defaultMessage: 'Pixel Type',
              id: 'conversionTracking.modal.pixelType.label'
            })}
            onChange={(value: PixelTypeOption[]) => handleFilterChange('pixelTypes', value)}
            options={pixelTypeOptions}
            placeholder={formatMessage({
              defaultMessage: 'Select Type/s',
              id: 'conversionTracking.filters.type'
            })}
            showDeselectOption
            showSearch={false}
            showSelectedItems={false}
            type="multiple"
            value={filters.pixelTypes}
          />
          <DateRange
            id="conversionTrackingModal__date_filter"
            onFilterStatus={handleDateRangeChange}
            value={filters.dates}
          />
          <InputSearch
            className="conversionTrackingModal__search"
            defaultValue={filters.name}
            id="conversionTrackingModal-searchByName"
            key={filters.name}
            lens
            name="pixelName"
            onChange={(value: string) => handleFilterChange('name', value)}
            placeholder={formatMessage({
              defaultMessage: 'Search by name, pixel type...',
              id: 'conversionTracking.table.filter.search'
            })}
          />
        </div>
        <div className="conversionTrackingModal__itemList">
          <TaptapList
            className="conversionTrackingModal__list"
            fetchData={fetchPixelsPaginated}
            id={`conversionTracking-table-${id}`}
            onSelectionChange={handleSelectionChange}
            selectionType="checkboxMiddle"
          >
            <TaptapList.ColumnsBar>
              <TaptapList.ColumnsBar.SelectAll />
              <TaptapList.ColumnsBar.Column
                className="pixelName"
                description={formatMessage({
                  defaultMessage: 'Pixel',
                  id: 'conversionTracking.table.columns.pixel'
                })}
                id="pixelName"
              />
              <TaptapList.ColumnsBar.Column
                className="type"
                description={formatMessage({
                  defaultMessage: 'Pixel Type',
                  id: 'conversionTracking.table.columns.pixelType'
                })}
                id="type"
              />
              <TaptapList.ColumnsBar.Column
                className="campaigns"
                description={formatMessage({
                  defaultMessage: 'Campaigns',
                  id: 'conversionTracking.table.columns.campaigns'
                })}
                id="campaigns"
              />
              <TaptapList.ColumnsBar.Column
                className="creationDate"
                description={formatMessage({
                  defaultMessage: 'Creation Data',
                  id: 'conversionTracking.table.columns.creationData'
                })}
                id="creationDate"
              />
            </TaptapList.ColumnsBar>
            <TaptapList.RowsList<TaptapListItem>
              emptyStateDescription={formatMessage({
                defaultMessage: 'Try adjusting your search or filters to find pixels.',
                id: 'conversionTracking.emptyState.description'
              })}
              emptyStateIcon="fal fa-empty-set"
              emptyStateTitle={formatMessage({
                defaultMessage: 'No results match your filters',
                id: 'conversionTracking.emptyState.title'
              })}
            >
              {itemsList =>
                itemsList.map(item => (
                  <TaptapList.RowsList.Row id={item.id} key={item.id}>
                    <span className="pixelName">{item.title}</span>
                    <span className="type">{item.type}</span>
                    <span className="campaigns">{item.campaigns}</span>
                    <span className="creationDate">{item.creationDate}</span>
                    <TaptapList.RowsList.Row.Actions actions={item.actions} />
                  </TaptapList.RowsList.Row>
                ))
              }
            </TaptapList.RowsList>
          </TaptapList>
        </div>
      </ModalUI>
    </CustomThemeProvider>,
    targetBody
  );
}
